// The medical record: timeline, medications, scripts and profile.
//
// These share a tab and a sub-navigation because they are one thing to the user —
// "my health information" — even though they are four tables. The brief is explicit
// that this must not become the centre of gravity (§57), so it stays one tab.

import { useCallback, useEffect, useState } from 'react';
import {
  api, type Allergy, type Clinician, type HealthEvent, type MedicalCondition,
  type Medication, type Prescription,
} from '../lib/api.ts';
import {
  Button, Card, DemoBadge, EmptyState, ErrorNote, Field, SectionTitle, Spinner, TextArea, TextInput,
} from '../components/ui.tsx';
import { longDate, relativeDate, titleCase, todayLocal } from '../lib/format.ts';

const SUB_TABS = [
  { id: 'timeline', label: 'Timeline', path: '/timeline' },
  { id: 'medications', label: 'Medications', path: '/medications' },
  { id: 'scripts', label: 'Scripts', path: '/scripts' },
  { id: 'profile', label: 'Profile', path: '/profile' },
];

export function RecordNav({ screen, navigate }: { screen: string; navigate: (path: string) => void }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1">
      {SUB_TABS.map((tab) => (
        <button
          key={tab.id}
          onClick={() => navigate(tab.path)}
          aria-current={screen === tab.id ? 'page' : undefined}
          className={`shrink-0 min-h-11 px-3.5 rounded-full border text-sm
            ${screen === tab.id ? 'bg-accent-soft border-accent' : 'bg-surface border-hairline text-ink-2'}`}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

// --- Timeline (brief §13) --------------------------------------------------

export function Timeline() {
  const [events, setEvents] = useState<HealthEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const today = todayLocal();

  useEffect(() => {
    void api.medical.timeline()
      .then((r) => setEvents(r.events))
      .catch((e) => setError((e as Error).message));
  }, []);

  if (error) return <ErrorNote message={error} />;
  if (!events) return <Spinner />;
  if (events.length === 0) {
    return <EmptyState title="Nothing on the timeline yet" detail="Experiments, medications and scripts appear here as they happen." />;
  }

  return (
    <ol className="space-y-2">
      {events.map((event) => (
        <li key={event.id}>
          <Card className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm font-medium">{event.title.replace(/^\[demo\]\s*/, '')}</p>
              <p className="text-xs text-muted mt-0.5">{titleCase(event.type)}</p>
              {event.description && <p className="text-sm text-ink-2 mt-1">{event.description}</p>}
            </div>
            <div className="text-right shrink-0">
              <p className="text-xs tabular-nums">{longDate(event.event_date)}</p>
              <p className="text-[11px] text-muted">{relativeDate(event.event_date, today)}</p>
            </div>
          </Card>
        </li>
      ))}
    </ol>
  );
}

// --- Medications (brief §14) -----------------------------------------------

export function Medications() {
  const [medications, setMedications] = useState<Medication[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setMedications((await api.medical.medications()).medications);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (error) return <ErrorNote message={error} />;
  if (!medications) return <Spinner />;

  const active = medications.filter((m) => m.active);
  const stopped = medications.filter((m) => !m.active);

  return (
    <div className="space-y-5">
      {adding
        ? <MedicationForm onDone={() => { setAdding(false); void load(); }} onCancel={() => setAdding(false)} />
        : <Button variant="primary" full onClick={() => setAdding(true)}>Add medication or supplement</Button>}

      {medications.length === 0 && !adding && (
        <EmptyState title="Nothing recorded" detail="Add what you take so it appears on your doctor summary." />
      )}

      {[{ title: 'Currently taking', items: active }, { title: 'Stopped', items: stopped }]
        .filter((g) => g.items.length > 0).map((group) => (
          <section key={group.title}>
            <SectionTitle>{group.title}</SectionTitle>
            <ul className="space-y-2">
              {group.items.map((medication) => (
                <li key={medication.id}>
                  <Card>
                    <div className="flex items-start gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{medication.name.replace(/^\[demo\]\s*/, '')}</p>
                        {/* The user's own recorded details, shown verbatim. */}
                        <p className="text-sm text-ink-2 mt-0.5">
                          {[medication.strength, medication.dose, medication.frequency].filter(Boolean).join(' · ') || 'No details recorded'}
                        </p>
                        {medication.purpose && <p className="text-xs text-muted mt-1">For {medication.purpose}</p>}
                        {medication.start_date && (
                          <p className="text-xs text-muted mt-1">
                            Started {longDate(medication.start_date)}
                            {medication.stop_date && ` · stopped ${longDate(medication.stop_date)}`}
                          </p>
                        )}
                      </div>
                      {medication.is_demo && <DemoBadge />}
                    </div>

                    <div className="flex gap-2 mt-3">
                      {medication.active && (
                        <Button onClick={() => {
                          void api.medical.updateMedication(medication.id, { active: false, stop_date: todayLocal() }).then(load);
                        }}>
                          Mark stopped
                        </Button>
                      )}
                      <Button variant="ghost" onClick={() => {
                        void api.medical.removeMedication(medication.id).then(load);
                      }}>
                        Delete
                      </Button>
                    </div>
                  </Card>
                </li>
              ))}
            </ul>
          </section>
        ))}
    </div>
  );
}

function MedicationForm({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [name, setName] = useState('');
  const [strength, setStrength] = useState('');
  const [dose, setDose] = useState('');
  const [frequency, setFrequency] = useState('');
  const [purpose, setPurpose] = useState('');
  const [prescriber, setPrescriber] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async (): Promise<void> => {
    setSaving(true);
    try {
      await api.medical.createMedication({
        name: name.trim(),
        strength: strength.trim() || null,
        dose: dose.trim() || null,
        frequency: frequency.trim() || null,
        purpose: purpose.trim() || null,
        prescriber: prescriber.trim() || null,
        start_date: todayLocal(),
        active: true,
      });
      onDone();
    } catch (e) {
      setError((e as Error).message);
      setSaving(false);
    }
  };

  return (
    <Card className="space-y-3">
      {error && <ErrorNote message={error} />}
      <Field label="Name" required><TextInput value={name} onChange={setName} placeholder="Medication or supplement" /></Field>
      <Field label="Strength"><TextInput value={strength} onChange={setStrength} placeholder="e.g. 20mg" /></Field>
      {/* Copied from the packaging by the user. Nothing here suggests a value. */}
      <Field label="Dose" hint="Exactly as written on your prescription or packaging.">
        <TextInput value={dose} onChange={setDose} placeholder="e.g. one tablet" />
      </Field>
      <Field label="How often"><TextInput value={frequency} onChange={setFrequency} placeholder="e.g. twice daily" /></Field>
      <Field label="What it is for"><TextInput value={purpose} onChange={setPurpose} /></Field>
      <Field label="Prescribed by"><TextInput value={prescriber} onChange={setPrescriber} /></Field>
      <div className="flex gap-2">
        <Button className="flex-1" onClick={onCancel}>Cancel</Button>
        <Button variant="primary" className="flex-1" disabled={!name.trim() || saving} onClick={() => void save()}>Save</Button>
      </div>
    </Card>
  );
}

// --- Scripts (brief §15) ---------------------------------------------------

export function Scripts() {
  const [prescriptions, setPrescriptions] = useState<Prescription[] | null>(null);
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [prescriber, setPrescriber] = useState('');
  const [expiry, setExpiry] = useState('');
  const [repeats, setRepeats] = useState('');
  const [error, setError] = useState<string | null>(null);
  const today = todayLocal();

  const load = useCallback(async () => {
    try {
      setPrescriptions((await api.medical.prescriptions()).prescriptions);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (error) return <ErrorNote message={error} />;
  if (!prescriptions) return <Spinner />;

  return (
    <div className="space-y-4">
      {adding ? (
        <Card className="space-y-3">
          <Field label="Medication" required><TextInput value={name} onChange={setName} /></Field>
          <Field label="Prescribed by"><TextInput value={prescriber} onChange={setPrescriber} /></Field>
          <Field label="Expires"><TextInput type="date" value={expiry} onChange={setExpiry} /></Field>
          <Field label="Repeats remaining"><TextInput value={repeats} onChange={setRepeats} inputMode="numeric" /></Field>
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => setAdding(false)}>Cancel</Button>
            <Button variant="primary" className="flex-1" disabled={!name.trim()} onClick={() => {
              void api.medical.createPrescription({
                medication_name: name.trim(),
                prescriber: prescriber.trim() || null,
                issued_date: today,
                expiry_date: expiry || null,
                repeats_remaining: repeats ? Number(repeats) : null,
              }).then(() => { setAdding(false); setName(''); setPrescriber(''); setExpiry(''); setRepeats(''); void load(); });
            }}>Save</Button>
          </div>
        </Card>
      ) : (
        <Button variant="primary" full onClick={() => setAdding(true)}>Add a script</Button>
      )}

      {prescriptions.length === 0 && !adding && (
        <EmptyState title="No scripts recorded" detail="Track expiry dates and repeats so a renewal never catches you out." />
      )}

      <ul className="space-y-2">
        {prescriptions.map((script) => {
          const expiringSoon = script.expiry_date && script.expiry_date <= relativeCutoff(today, 30);
          return (
            <li key={script.id}>
              <Card>
                <div className="flex items-start gap-2">
                  <div className="flex-1">
                    <p className="font-medium">{script.medication_name.replace(/^\[demo\]\s*/, '')}</p>
                    {script.prescriber && <p className="text-xs text-muted mt-0.5">{script.prescriber}</p>}
                    <p className="text-sm mt-1.5">
                      {script.expiry_date
                        ? <span className={expiringSoon ? 'text-serious' : ''}>
                            Expires {longDate(script.expiry_date)} ({relativeDate(script.expiry_date, today)})
                          </span>
                        : <span className="text-muted">No expiry recorded</span>}
                    </p>
                    {script.repeats_remaining !== null && (
                      <p className="text-xs text-muted mt-0.5">{script.repeats_remaining} repeats left</p>
                    )}
                  </div>
                  {script.is_demo && <DemoBadge />}
                </div>
                <Button variant="ghost" className="mt-2" onClick={() => {
                  void api.medical.removePrescription(script.id).then(load);
                }}>Delete</Button>
              </Card>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function relativeCutoff(today: string, days: number): string {
  const p = { y: Number(today.slice(0, 4)), m: Number(today.slice(5, 7)), d: Number(today.slice(8, 10)) };
  return new Date(Date.UTC(p.y, p.m - 1, p.d) + days * 86_400_000).toISOString().slice(0, 10);
}

// --- Profile (brief §16) ---------------------------------------------------

export function Profile() {
  const [data, setData] = useState<{ conditions: MedicalCondition[]; allergies: Allergy[]; clinicians: Clinician[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<{ kind: string; a: string; b: string } | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.medical.profile());
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  if (error) return <ErrorNote message={error} />;
  if (!data) return <Spinner />;

  const save = async (): Promise<void> => {
    if (!draft?.a.trim()) return;
    if (draft.kind === 'condition') await api.medical.createCondition({ name: draft.a.trim(), notes: draft.b.trim() || null });
    if (draft.kind === 'allergy') await api.medical.createAllergy({ substance: draft.a.trim(), reaction: draft.b.trim() || null });
    if (draft.kind === 'clinician') await api.medical.createClinician({ name: draft.a.trim(), speciality: draft.b.trim() || null });
    setDraft(null);
    await load();
  };

  const sections = [
    { kind: 'condition', title: 'Conditions', items: data.conditions.map((c) => ({ id: c.id, primary: c.name, secondary: c.status })), remove: api.medical.removeCondition, labelA: 'Condition', labelB: 'Notes' },
    { kind: 'allergy', title: 'Allergies', items: data.allergies.map((a) => ({ id: a.id, primary: a.substance, secondary: a.reaction })), remove: api.medical.removeAllergy, labelA: 'Substance', labelB: 'Reaction' },
    { kind: 'clinician', title: 'Doctors & specialists', items: data.clinicians.map((c) => ({ id: c.id, primary: c.name, secondary: c.speciality })), remove: api.medical.removeClinician, labelA: 'Name', labelB: 'Speciality' },
  ];

  return (
    <div className="space-y-6">
      {sections.map((section) => (
        <section key={section.kind}>
          <SectionTitle
            action={
              <button className="text-xs text-accent min-h-11"
                onClick={() => setDraft(draft?.kind === section.kind ? null : { kind: section.kind, a: '', b: '' })}>
                {draft?.kind === section.kind ? 'Cancel' : 'Add'}
              </button>
            }
          >
            {section.title}
          </SectionTitle>

          {draft?.kind === section.kind && (
            <Card className="space-y-3 mb-2">
              <Field label={section.labelA} required>
                <TextInput value={draft.a} onChange={(v) => setDraft({ ...draft, a: v })} />
              </Field>
              <Field label={section.labelB}>
                <TextInput value={draft.b} onChange={(v) => setDraft({ ...draft, b: v })} />
              </Field>
              <Button variant="primary" full disabled={!draft.a.trim()} onClick={() => void save()}>Save</Button>
            </Card>
          )}

          {section.items.length === 0 ? (
            <p className="text-sm text-muted">Nothing recorded.</p>
          ) : (
            <ul className="space-y-2">
              {section.items.map((item) => (
                <li key={item.id}>
                  <Card className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-sm font-medium">{item.primary}</p>
                      {item.secondary && <p className="text-xs text-muted mt-0.5">{item.secondary}</p>}
                    </div>
                    <Button variant="ghost" onClick={() => { void section.remove(item.id).then(load); }}>Remove</Button>
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
      ))}

      <Notes />
    </div>
  );
}

function Notes() {
  const [notes, setNotes] = useState<Array<{ id: string; title: string | null; body: string; local_date: string }> | null>(null);
  const [body, setBody] = useState('');
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setNotes((await api.medical.notes()).notes);
  }, []);

  useEffect(() => { void load(); }, [load]);

  return (
    <section>
      <SectionTitle
        action={<button className="text-xs text-accent min-h-11" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : 'Add'}</button>}
      >
        Notes
      </SectionTitle>

      {adding && (
        <Card className="space-y-3 mb-2">
          <TextArea value={body} onChange={setBody} rows={4} placeholder="Anything worth remembering." />
          <Button variant="primary" full disabled={!body.trim()} onClick={() => {
            void api.medical.createNote({ local_date: todayLocal(), body: body.trim() })
              .then(() => { setBody(''); setAdding(false); void load(); });
          }}>Save note</Button>
        </Card>
      )}

      {notes && notes.length > 0 && (
        <ul className="space-y-2">
          {notes.slice(0, 10).map((note) => (
            <li key={note.id}>
              <Card>
                <p className="text-xs text-muted">{longDate(note.local_date)}</p>
                {note.title && <p className="text-sm font-medium mt-0.5">{note.title.replace(/^\[demo\]\s*/, '')}</p>}
                <p className="text-sm mt-1 whitespace-pre-wrap">{note.body}</p>
              </Card>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
